import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Download, Trash2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AccountPrivacy() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center text-slate-400">
        Please <Link to="/login" className="text-[#00E5A3] underline">sign in</Link> to manage your account and privacy.
      </div>
    );
  }

  const exportData = async () => {
    setExporting(true);
    try {
      const r = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sidelineiq-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch { toast.error("Could not export your data"); }
    finally { setExporting(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/me");
      toast.success("Your account and data have been deleted");
      await logout();
      navigate("/");
    } catch { toast.error("Could not delete your account"); setDeleting(false); }
  };

  return (
    <div data-testid="account-privacy-page" className="max-w-[760px] mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 flex items-center gap-2 mb-1">
        <ShieldCheck size={22} className="text-[#00E5A3]" /> Account & Privacy
      </h1>
      <p className="text-sm text-slate-400 mb-6">Signed in as <span className="text-slate-200">{user.email}</span> · {user.tier} tier</p>

      <div className="space-y-4">
        <section className="bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
          <h2 className="font-heading text-lg font-bold text-slate-100 mb-1">Do Not Sell or Share My Info</h2>
          <p className="text-sm text-slate-400">
            SidelineIQ <b className="text-slate-200">does not sell or share</b> your personal information with third parties for
            advertising or any other purpose. We only use it to run your account, watchlists and alerts. There's nothing to opt out of —
            but you can export or delete everything below at any time.
          </p>
        </section>

        <section className="bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-heading text-lg font-bold text-slate-100 mb-1">Export my data</h2>
              <p className="text-sm text-slate-400 max-w-md">Download a JSON copy of everything we hold for your account — profile, watchlists and alert history.</p>
            </div>
            <button data-testid="export-data-btn" onClick={exportData} disabled={exporting}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#151B2B] text-slate-100 text-sm font-semibold hover:bg-[#1C2438] disabled:opacity-50 transition-colors shrink-0">
              <Download size={15} /> {exporting ? "Preparing…" : "Download"}
            </button>
          </div>
        </section>

        <section className="bg-[#F87171]/5 border border-[#F87171]/30 rounded-xl p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-heading text-lg font-bold text-[#FCA5A5] mb-1 flex items-center gap-1.5"><AlertTriangle size={17} /> Delete my account</h2>
              <p className="text-sm text-slate-400 max-w-md">Permanently erase your account, watchlists and alerts. This cannot be undone.</p>
            </div>
            {!confirming ? (
              <button data-testid="delete-account-btn" onClick={() => setConfirming(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F87171] text-[#1A0606] text-sm font-semibold hover:opacity-90 transition-opacity shrink-0">
                <Trash2 size={15} /> Delete account
              </button>
            ) : (
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid="delete-cancel-btn" onClick={() => setConfirming(false)}
                  className="px-3 py-2 rounded-lg bg-[#151B2B] text-slate-300 text-sm hover:bg-[#1C2438] transition-colors">Cancel</button>
                <button data-testid="delete-confirm-btn" onClick={deleteAccount} disabled={deleting}
                  className="px-4 py-2 rounded-lg bg-[#F87171] text-[#1A0606] text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
                  {deleting ? "Deleting…" : "Yes, delete everything"}
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      <p className="text-[11px] text-slate-600 mt-6">
        See our <Link to="/privacy" className="text-slate-400 hover:text-[#00E5A3] underline">Privacy Policy</Link> and{" "}
        <Link to="/responsible-gaming" className="text-slate-400 hover:text-[#00E5A3] underline">Responsible Gaming</Link> resources.
      </p>
    </div>
  );
}
